import React from 'react';
import {
  Box,
  createStyles,
  useMediaQuery,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { CreateButton, SecondarySidebar } from '../UI';
import { RootState } from '../../store';
import { FormModal } from '../Modals';
import { useChannelForm, ChannelType } from './useChannelForm';
import BaseTypography from '../Text/BaseTypography';
import { BlackHeadings } from '../../theme/colors';

import { useIntakeSendForm } from '../IntakeForms/useIntakeSendForm';
import { useGetChannelModalTexts } from './useGetChannelModalTexts';
import { CreateButtonProps } from '../UI/Buttons/CreateButton';

export const channelHeaderHeightDesktop = 86;
export const channelHeaderHeightMobile = 36;
const CREATE_CHANNEL_FALLBACK_TITLE = 'Create a new channel';
const useStyles = makeStyles(theme =>
  createStyles({
    sidebarHeader: {
      color: BlackHeadings,
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1.5, 0),
      },
      [theme.breakpoints.up('sm')]: {
        height: channelHeaderHeightDesktop,
        width: `calc(100% - ${theme.spacing(1)}px)`,
      },
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    createButton: {
      align: 'flex-end',
    },
  }),
);
export interface ChannelsSidebarProps {
  entity: ChannelType;
  onCreate: (values: any) => void;
  initialCreateFormValue: any;
  title?: string;
  modalTitle?: string;
  hasChannels?: boolean;
  newButtonProps?: Partial<CreateButtonProps>;
  newChannelAction?: string;
  onSecondaryOptionClick: (option: string) => void;
  noCreateAction?: boolean;
}

const ChannelsSidebar: React.FC<ChannelsSidebarProps> = ({
  onCreate,
  entity,
  children,
  initialCreateFormValue,
  title = null,
  modalTitle,
  hasChannels = true,
  newButtonProps,
  newChannelAction,
  onSecondaryOptionClick,
  noCreateAction = false,
}) => {
  const theme = useTheme();
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const moduleSettings = useSelector(
    (state: RootState) => state.settings.moduleSettings,
  );
  const [openCreateModal, setOpenCreateModal] = React.useState(false);

  const handleOpenModal = () => setOpenCreateModal(true);
  const handleCloseModal = () => setOpenCreateModal(false);
  const { title: pageTitle, positiveAction } = useGetChannelModalTexts(
    entity,
    moduleSettings,
  );

  // According to the entity type return a specific
  // form for the modal
  const buildChannelFormByEntity = () => {
    if (entity === 'forms') {
      return useIntakeSendForm();
    }

    return useChannelForm(entity);
  };

  /**
   * Based on the action that is selected in menu
   * we should perform new channel action to open modal
   * or call prop function to handle secondary option
   * @param optionValue menu option selected
   */
  const handleClickOptions = (optionValue: string) => {
    if (optionValue === newChannelAction) {
      handleOpenModal();
    } else {
      onSecondaryOptionClick(optionValue);
    }
  };

  const createButtonProps = newButtonProps
    ? {
        onClickOption: handleClickOptions,
        ...newButtonProps,
      }
    : {
        onClick: handleOpenModal,
      };

  return (
    <SecondarySidebar hidden={!hasChannels}>
      <div className={classes.sidebarHeader}>
        {isSmallWidth ? (
          <BaseTypography fontType="18Medium">
            {title || pageTitle}
          </BaseTypography>
        ) : (
          <BaseTypography fontType="24Medium">
            {title || pageTitle}
          </BaseTypography>
        )}

        {!isClient && hasChannels && !noCreateAction && (
          <CreateButton
            className={classes.createButton}
            iconOnly
            {...createButtonProps}
          />
        )}
      </div>
      {children}
      <FormModal
        buttonLabel={positiveAction}
        initialFormValue={initialCreateFormValue}
        entityName="channel"
        handleSave={onCreate}
        useFormHook={() => buildChannelFormByEntity()}
        open={openCreateModal}
        onClose={handleCloseModal}
        createButtonProps={{ fullWidth: true }}
        title={modalTitle || CREATE_CHANNEL_FALLBACK_TITLE}
      />
    </SecondarySidebar>
  );
};

export default React.memo(ChannelsSidebar);
